import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DashboardLayout from './DashboardLayout';
import { SearchableSelect } from './SearchableSelect';
import { useAuth } from '../../context/AuthContext';
import {
  useGetAccountableDepartmentsQuery,
  useGetActiveCategoriesQuery,
  useGetActiveSubcategoriesByCategoryQuery,
  useGetIssueTypeByIdQuery,
  useUpdateNatureOfComplaintMutation,
} from '../../redux/complaintApi';
import { validateRequired } from '../../utils/validation';
import { issueTypeListBackPath } from '../../utils/issueTypeListState';

function pickRecordId(record, keys) {
  for (const key of keys) {
    const parts = key.split('.');
    let value = record;
    for (const part of parts) {
      if (value == null || typeof value !== 'object') {
        value = undefined;
        break;
      }
      value = value[part];
    }
    if (value != null && value !== '' && typeof value !== 'object') {
      return String(value);
    }
  }
  return '';
}

const inputClasses =
  'mt-1 w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';

function AdminNatureOfComplaintEdit() {
  const { area } = useAuth();
  const isAdmin = area === 'admin';
  const navigate = useNavigate();
  const location = useLocation();
  const listPath = issueTypeListBackPath(location);
  const { id } = useParams();
  const rowId = Number(id);

  const {
    data: issueTypeRes,
    isFetching,
    isError,
  } = useGetIssueTypeByIdQuery(rowId, { skip: !Number.isFinite(rowId) });
  const [updateNatureOfComplaint, { isLoading }] =
    useUpdateNatureOfComplaintMutation();

  const record = useMemo(() => {
    const raw = issueTypeRes?.data ?? issueTypeRes;
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    return raw;
  }, [issueTypeRes]);

  const [form, setForm] = useState({
    name: '',
    departmentId: '',
    categoryId: '',
    subcategoryId: '',
  });
  const [errors, setErrors] = useState({});

  const { data: departmentsRes } = useGetAccountableDepartmentsQuery();
  const { data: categoriesRes } = useGetActiveCategoriesQuery(
    { accountable_department_id: form.departmentId },
    { skip: !form.departmentId }
  );
  const { data: subcategoriesRes } = useGetActiveSubcategoriesByCategoryQuery(
    form.categoryId,
    { skip: !form.categoryId }
  );

  const departmentOptions = useMemo(() => {
    const list = departmentsRes?.data || departmentsRes || [];
    return (Array.isArray(list) ? list : []).map((dept) => ({
      value: String(dept.id),
      label: dept.name ?? String(dept.id),
    }));
  }, [departmentsRes]);

  const categoryOptions = useMemo(() => {
    const list = categoriesRes?.data || categoriesRes || [];
    return (Array.isArray(list) ? list : []).map((cat) => ({
      value: String(cat.id),
      label: cat.name ?? String(cat.id),
    }));
  }, [categoriesRes]);

  const subcategoryOptions = useMemo(() => {
    const list = subcategoriesRes?.data || subcategoriesRes || [];
    return (Array.isArray(list) ? list : []).map((s) => ({
      value: String(s.id),
      label: s.name ?? String(s.id),
    }));
  }, [subcategoriesRes]);

  const reqLabel = (text) => (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-700">
      <span className="min-w-0 leading-snug">{text}</span>
      <span className="shrink-0 text-base leading-none text-red-600" aria-hidden="true">
        *
      </span>
    </span>
  );

  const setField = (field) => (e) => {
    const value = e?.target?.value ?? '';
    setForm((p) => {
      if (field === 'departmentId') {
        return { ...p, departmentId: value, categoryId: '', subcategoryId: '' };
      }
      if (field === 'categoryId') {
        return { ...p, categoryId: value, subcategoryId: '' };
      }
      return { ...p, [field]: value };
    });
    setErrors((prev) => {
      const next = { ...prev, [field]: '' };
      if (field === 'departmentId') {
        next.categoryId = '';
        next.subcategoryId = '';
      } else if (field === 'categoryId') {
        next.subcategoryId = '';
      }
      return next;
    });
  };

  const validateForm = () => {
    const next = {};
    const deptErr = validateRequired(form.departmentId, 'Accountable Department');
    if (deptErr) next.departmentId = deptErr;
    const catErr = validateRequired(form.categoryId, 'Category');
    if (catErr) next.categoryId = catErr;
    const subErr = validateRequired(form.subcategoryId, 'Sub Category');
    if (subErr) next.subcategoryId = subErr;
    const nameErr = validateRequired(form.name, 'Name');
    if (nameErr) next.name = nameErr;
    setErrors(next);
    const keys = Object.keys(next);
    if (keys.length === 0) return { ok: true };
    return { ok: false, firstError: next[keys[0]] };
  };

  useEffect(() => {
    if (!record) return;
    setForm({
      name: String(record.name ?? '').trim(),
      departmentId: pickRecordId(record, [
        'accountable_department_id',
        'accountableDepartmentId',
        'accountable_department.id',
        'department_id',
        'department.id',
        'category.accountable_department_id',
        'subcategory.category.accountable_department_id',
      ]),
      categoryId: pickRecordId(record, [
        'category_id',
        'categoryId',
        'category.id',
        'subcategory.category_id',
        'subcategory.category.id',
      ]),
      subcategoryId: pickRecordId(record, [
        'subcategory_id',
        'subcategoryId',
        'subcategory.id',
      ]),
    });
  }, [record]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validation = validateForm();
    if (!validation.ok) {
      toast.error(
        validation.firstError || 'Please correct the highlighted fields.'
      );
      return;
    }

    try {
      await updateNatureOfComplaint({
        id: rowId,
        name: form.name.trim(),
        accountable_department_id: Number(form.departmentId),
        category_id: Number(form.categoryId),
        subcategory_id: Number(form.subcategoryId),
      }).unwrap();
      toast.success('Updated successfully.');
      navigate(listPath);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Update issue type failed:', err);
      toast.error(err?.data?.message || 'Failed to update.');
    }
  };

  if ((!isFetching && !record) || isError || !Number.isFinite(rowId)) {
    return (
      <DashboardLayout title="Edit Issue Type">
        <div className="space-y-4 py-4">
          <div className="rounded-2xl bg-white p-4 shadow-sm sm:p-6">
            <p className="text-sm text-gray-600">Record not found.</p>
            <Link
              to={listPath}
              className="mt-4 inline-block text-sm font-semibold text-brand hover:underline"
            >
              Back to list
            </Link>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Issue Type">
      <div className="space-y-4 py-4">
        <div className="flex items-center justify-end gap-4">
          <div className="flex items-center gap-4">
            <p className="text-sm text-gray-500">
              <Link
                to={isAdmin ? '/dashboard/admin' : '/dashboard/employee'}
                className="text-brand hover:underline"
              >
                Home
              </Link>
              {' / '}
              <Link
                to={listPath}
                className="text-brand hover:underline"
              >
                Issue Type
              </Link>
              {' / '}
              <span className="text-gray-400">Edit</span>
            </p>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-2xl bg-white p-4 shadow-sm sm:p-6"
        >
          <h2 className="mb-4 text-base font-semibold text-gray-900">
            Issue Type Information
          </h2>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="flex flex-col text-sm text-gray-700">
              {reqLabel('Accountable Department')}
              <SearchableSelect
                name="departmentId"
                value={form.departmentId}
                onChange={setField('departmentId')}
                placeholder="Select department"
                className={`${inputClasses} ${
                  errors?.departmentId ? 'border-red-500' : ''
                }`}
                options={departmentOptions}
              />
              {errors?.departmentId ? (
                <span className="mt-1 text-xs text-red-600">
                  {errors.departmentId}
                </span>
              ) : null}
            </label>

            <label className="flex flex-col text-sm text-gray-700">
              {reqLabel('Category')}
              <SearchableSelect
                name="categoryId"
                value={form.categoryId}
                onChange={setField('categoryId')}
                placeholder="Select category"
                disabled={!form.departmentId}
                className={`${inputClasses} ${
                  errors?.categoryId ? 'border-red-500' : ''
                } disabled:cursor-not-allowed disabled:opacity-60`}
                options={categoryOptions}
              />
              {errors?.categoryId ? (
                <span className="mt-1 text-xs text-red-600">
                  {errors.categoryId}
                </span>
              ) : null}
            </label>

            <label className="flex flex-col text-sm text-gray-700">
              {reqLabel('Sub Category')}
              <SearchableSelect
                name="subcategoryId"
                value={form.subcategoryId}
                onChange={setField('subcategoryId')}
                placeholder="Select sub category"
                disabled={!form.categoryId}
                className={`${inputClasses} ${
                  errors?.subcategoryId ? 'border-red-500' : ''
                } disabled:cursor-not-allowed disabled:opacity-60`}
                options={subcategoryOptions}
              />
              {errors?.subcategoryId ? (
                <span className="mt-1 text-xs text-red-600">
                  {errors.subcategoryId}
                </span>
              ) : null}
            </label>

            <label className="flex flex-col text-sm text-gray-700">
              {reqLabel('Name')}
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={setField('name')}
                placeholder="Enter name"
                className={`${inputClasses} ${errors?.name ? 'border-red-500' : ''}`}
              />
              {errors?.name ? (
                <span className="mt-1 text-xs text-red-600">{errors.name}</span>
              ) : null}
            </label>
          </div>

          <div className="mt-6 flex justify-start gap-5">
            <button
              type="submit"
              disabled={isLoading || isFetching}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg bg-brand px-8 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-800 disabled:opacity-60"
            >
              {isLoading ? 'Saving...' : 'Update'}
            </button>

            <button
              type="button"
              onClick={() => navigate(listPath)}
              className="inline-flex min-w-[140px] items-center justify-center rounded-lg border border-brand bg-white px-8 py-2.5 text-sm font-semibold text-brand hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}

export default AdminNatureOfComplaintEdit;
